import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { BeatDto } from '../../api/queries/storyArchitect';

export interface BeatTimelineProps {
  beats: BeatDto[];
  selectedBeatId: string | null;
  onSelectBeat: (id: string) => void;
  onReorderBeat: (id: string, newIndex: number) => void;
}

const BEAT_TYPE_COLORS: Record<
  string,
  'info' | 'warning' | 'success' | 'secondary' | 'error' | 'primary' | 'default'
> = {
  setup: 'info',
  inciting_incident: 'warning',
  rising_action: 'success',
  midpoint: 'secondary',
  climax: 'error',
  falling_action: 'warning',
  resolution: 'primary',
  custom: 'default',
};

function beatTypeColor(
  beatType: string | null | undefined,
):
  | 'info'
  | 'warning'
  | 'success'
  | 'secondary'
  | 'error'
  | 'primary'
  | 'default' {
  if (!beatType) return 'default';
  return BEAT_TYPE_COLORS[beatType] ?? 'default';
}

function beatTypeLabel(beatType: string | null | undefined): string {
  if (!beatType) return 'beat';
  return beatType.replace(/_/g, ' ');
}

/**
 * Three-act horizontal timeline rendering beats as cards grouped into Act 1, Act 2, Act 3.
 */
export function BeatTimeline({
  beats,
  selectedBeatId,
  onSelectBeat,
  onReorderBeat,
}: BeatTimelineProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const { act1, act2, act3 } = useMemo(() => {
    const sorted = [...beats].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    const total = sorted.length;
    const firstCut = Math.ceil(total / 3);
    const secondCut = Math.ceil((total * 2) / 3);
    return {
      act1: sorted.slice(0, firstCut),
      act2: sorted.slice(firstCut, secondCut),
      act3: sorted.slice(secondCut),
    };
  }, [beats]);

  const allIds = useMemo(() => beats.map((b) => b.id), [beats]);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const allSorted = [...beats].sort(
      (a, b) => a.sequenceOrder - b.sequenceOrder,
    );
    const oldIndex = allSorted.findIndex((b) => b.id === String(active.id));
    const newIndex = allSorted.findIndex((b) => b.id === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorderBeat(String(active.id), newIndex);
  };

  const defined = beats.length;
  const target = Math.max(defined, 8);
  const progress = target > 0 ? (defined / target) * 100 : 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {defined} / {target} beats defined
        </Typography>
        <Box sx={{ flex: 1, maxWidth: 200 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(progress, 100)}
          />
        </Box>
      </Stack>

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}>
              <ActColumn
                label="Act 1 -- Setup"
                beats={act1}
                selectedBeatId={selectedBeatId}
                onSelectBeat={onSelectBeat}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <ActColumn
                label="Act 2 -- Confrontation"
                beats={act2}
                selectedBeatId={selectedBeatId}
                onSelectBeat={onSelectBeat}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <ActColumn
                label="Act 3 -- Resolution"
                beats={act3}
                selectedBeatId={selectedBeatId}
                onSelectBeat={onSelectBeat}
              />
            </Grid>
          </Grid>
        </SortableContext>
      </DndContext>
    </Box>
  );
}

function ActColumn({
  label,
  beats,
  selectedBeatId,
  onSelectBeat,
}: {
  label: string;
  beats: BeatDto[];
  selectedBeatId: string | null;
  onSelectBeat: (id: string) => void;
}) {
  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ mb: 1, display: 'block' }}
      >
        {label}
      </Typography>
      <Stack spacing={1}>
        {beats.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            No beats in this act
          </Typography>
        )}
        {beats.map((beat) => (
          <SortableBeatCard
            key={beat.id}
            beat={beat}
            selected={beat.id === selectedBeatId}
            onClick={() => onSelectBeat(beat.id)}
          />
        ))}
      </Stack>
    </Box>
  );
}

function SortableBeatCard({
  beat,
  selected,
  onClick,
}: {
  beat: BeatDto;
  selected: boolean;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: beat.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const hasLinkedParagraphs = beat.linkedParagraphIds.length > 0;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      variant="outlined"
      sx={{
        cursor: 'grab',
        borderColor: selected ? 'primary.main' : undefined,
        borderWidth: selected ? 2 : 1,
        bgcolor: selected ? 'action.selected' : undefined,
      }}
    >
      <CardActionArea onClick={onClick}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Typography variant="subtitle2" fontWeight="bold" noWrap>
            {beat.name}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, mb: 0.5 }}>
            <Chip
              label={beatTypeLabel(beat.beatType)}
              color={beatTypeColor(beat.beatType)}
              size="small"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
            {hasLinkedParagraphs && (
              <Chip
                label={`${beat.linkedParagraphIds.length} para.`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Stack>
          {beat.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.3,
              }}
            >
              {beat.description}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
